/**
 * resizeMixin —— 窗口尺寸变化监听（Vue 2）
 *
 * 功能：
 *   1. 在 mounted 时自动注册 window.resize 监听
 *   2. 内置 150ms 防抖，避免高频触发回调
 *   3. 尺寸稳定后触发 this.handleResize(width, height) 回调（由业务组件实现）
 *   4. beforeDestroy 时自动移除监听，无内存泄漏
 *   5. mounted 后立即触发一次回调，方便业务组件拿到初始尺寸
 *
 * 使用示例：
 *
 *   import resizeMixin from '@/mixins/resizeMixin'
 *
 *   export default {
 *     mixins: [resizeMixin],
 *     methods: {
 *       // 实现该方法（方法名固定为 handleResize），尺寸稳定后自动被调用
 *       handleResize(width, height) {
 *         console.log('窗口尺寸变化:', width, height)
 *         this.isMobile = width <= 768
 *       }
 *     }
 *   }
 *
 * 任意时刻也可主动获取当前尺寸：
 *   this.$_resizeMixin_getSize()   // 返回 { width, height }
 */

// 防抖阈值：150ms（连续 resize 只执行最后一次）
const DEBOUNCE_DELAY = 150

// 简易防抖：返回包裹后的函数，连续触发只执行最后一次
function debounce(fn, delay) {
    let timer = null
    return function () {
        const ctx = this
        const args = arguments
        if (timer) clearTimeout(timer)
        timer = setTimeout(function () {
            fn.apply(ctx, args)
        }, delay)
    }
}

export default {
    mounted() {
        const self = this

        // 用防抖包裹实际回调，handler 作为普通实例属性挂载（非 data，避免 Vue 保留前缀限制）
        this.$_resizeHandler = debounce(function () {
            const w = window.innerWidth || document.documentElement.clientWidth || 0
            const h = window.innerHeight || document.documentElement.clientHeight || 0
            // 触发业务组件的回调（存在才调用）
            if (typeof self.handleResize === 'function') {
                self.handleResize(w, h)
            }
        }, DEBOUNCE_DELAY)

        window.addEventListener('resize', this.$_resizeHandler)

        // mounted 后立即触发一次，让业务组件拿到初始尺寸
        this.$_resizeHandler()
    },
    beforeDestroy() {
        // 组件销毁时移除监听，防止内存泄漏
        if (this.$_resizeHandler) {
            window.removeEventListener('resize', this.$_resizeHandler)
            this.$_resizeHandler = null
        }
    },
    methods: {
        /**
         * 获取当前窗口尺寸（任何时候可调用）
         * @returns {{ width: number, height: number }}
         */
        $_resizeMixin_getSize() {
            return {
                width: window.innerWidth || document.documentElement.clientWidth || 0,
                height: window.innerHeight || document.documentElement.clientHeight || 0
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/mixins/resizeMixin.js